
import { useState } from 'react';
import * as XLSX from 'xlsx';
import { CreateEcommercePriceInput } from '@/types/database/ecommerce-prices';

export const useEcommerceExcelParser = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parseExcel = async (file: File): Promise<CreateEcommercePriceInput[]> => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const parsedData: CreateEcommercePriceInput[] = [];

      for (const row of jsonData) {
        try {
          // Handle different possible column names
          const rowData = row as any;
          
          // Extract date
          let date = rowData['Date'] || rowData['date'] || rowData['DATE'];
          if (typeof date === 'number') {
            // Excel date number to JavaScript date
            const excelDate = new Date((date - 25569) * 86400 * 1000);
            date = excelDate.toISOString().split('T')[0];
          } else if (date instanceof Date) {
            date = date.toISOString().split('T')[0];
          } else if (typeof date === 'string') {
            // Ensure date is in YYYY-MM-DD format
            const parsedDate = new Date(date);
            if (!isNaN(parsedDate.getTime())) {
              date = parsedDate.toISOString().split('T')[0];
            }
          }

          // Extract other fields
          const product_name = rowData['Product Name'] || rowData['product_name'] || rowData['PRODUCT_NAME'] || '';
          const price = parseFloat(rowData['Price'] || rowData['price'] || rowData['PRICE'] || '0');
          const location = rowData['Location'] || rowData['location'] || rowData['LOCATION'] || '';
          const product_remark = rowData['Product Remark'] || rowData['product_remark'] || rowData['PRODUCT_REMARK'] || '';

          if (!date || !product_name || !location || isNaN(price)) {
            console.warn('Skipping invalid row:', rowData);
            continue;
          }

          parsedData.push({
            date,
            product_name,
            price,
            location,
            product_remark: product_remark || undefined,
          });
        } catch (rowError) {
          console.error('Error parsing row:', row, rowError);
        }
      }

      if (parsedData.length === 0) {
        throw new Error('No valid data found in the Excel file');
      }

      return parsedData;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to parse Excel file';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return { parseExcel, isLoading, error };
};
